import React, { useState, useEffect } from 'react';
import { professionalService } from '../services/professionalService';
import { Appointment, AppointmentCalendarData } from '../types/professionalTypes';

interface AgendaProps {
  professionalId: string;
  onNavigate: (component: string) => void;
}

const Agenda: React.FC<AgendaProps> = ({ professionalId, onNavigate }) => {
  const [calendarData, setCalendarData] = useState<AppointmentCalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');

  useEffect(() => {
    loadCalendarData();
  }, [professionalId]);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      const data = await professionalService.getAppointments(professionalId);
      setCalendarData(data);
    } catch (error) {
      console.error('Erro ao carregar agenda:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      in_progress: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      completed: 'bg-gray-100 text-gray-800 border-gray-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      rescheduled: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityIcon = (priority: string) => {
    const icons = {
      low: '🔵',
      normal: '🟡',
      high: '🟠',
      emergency: '🔴'
    };
    return icons[priority as keyof typeof icons] || '🟡';
  };

  const getAppointmentTypeIcon = (type: string) => {
    const icons = {
      scheduled_visit: '📅',
      emergency_call: '🚨',
      consultation: '🩺',
      routine_check: '✅',
      vaccination: '💉',
      treatment: '🔬'
    };
    return icons[type as keyof typeof icons] || '📅';
  };

  const getAppointmentsForDate = (date: string) => {
    if (!calendarData) return [];
    return calendarData.appointments.filter(apt => apt.scheduling.scheduled_date === date);
  };

  const getWeekDates = (startDate: Date) => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const getCurrentWeekStart = () => {
    const today = new Date(selectedDate);
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    return monday;
  };

  const weekDates = getWeekDates(getCurrentWeekStart());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Carregando agenda...</span>
      </div>
    );
  }

  if (!calendarData) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Erro ao carregar agenda</p>
        <button 
          onClick={loadCalendarData}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Tentar Novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Minha Agenda</h1>
      <p className="text-gray-600">Componente em desenvolvimento</p>
    </div>
  );
};

export default Agenda; 